import * as React from 'react';
import parseMax from 'libphonenumber-js/max';
import axios from 'axios';
import Fuse from 'fuse.js';
import { FixedSizeList } from 'react-window';
import { useChatContext } from 'stream-chat-react';
import { GlobalContext } from '../GlobalState';
import { ChannelContainer } from '../components/ChannelContainer';
import { PlayNotificationSound, SideBarLogoXpel } from '../assets';
import { countAllUnreadChannels, countUnreadPerChannel, getPrimeName, notificationText, prime_badgeColor, sort_channels_to_list, sort_channels_to_list_prime } from '../utils';
import { CampaignModal, DirectAddModal, PrimeAddModal, TeamAddModal } from '../components/Modals';
import { ChipUnitCodes } from '../components/ChipUnitCodes';
import {
  Avatar, Badge, Box, Chip, CssBaseline, Divider, Drawer as MuiDrawer,IconButton, InputBase,
  List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText,
  Tooltip, Typography, useMediaQuery
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  Inventory as ArchiveIcon, Clear as ClearIcon, FilterList as FilterIcon,
  Groups as GroupsIcon, Logout as LogoutIcon, People as PeopleIcon, SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { SettingsModal } from './Modals/SettingsModal';
import { useLocalStorage } from '../utils/useLocalStorage';


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, drawer_width, is_desktop }) => ({
    flexGrow: 1,
    padding: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawer_width}px`,
    width: is_desktop === 'true' ? '100%' : drawer_width,
    zIndex: theme.zIndex.drawer -1,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const ChatDashboard = () => {
  const { globalState, setGlobalState } = React.useContext(GlobalContext);
  const { client, channel, setActiveChannel } = useChatContext();
  const [ appSettings, setAppSettings ] = useLocalStorage('xpel-chat-settings');
  const theme = useTheme();
  const is_desktop = useMediaQuery('(min-width:600px)', { noSsr: true });
  const parentBoxRef = React.useRef(null);
  const [drawerWidth, setDrawerWidth] = React.useState(380);
  const [badgeCounts, setBadgeCounts] = React.useState({ prime: 0, team: 0, direct: 0 });
  const processActiveChannels = (activeChannels) => {
    try {
      delete activeChannels['prime:custom_events'];
      delete globalState.idxs.ch_list['prime:custom_events'];
      let channels_to_list = Object.values({...globalState.idxs.ch_list, ...activeChannels}).filter(ch_item => ch_item.cid.startsWith(globalState.drawer.tab) === true);
      // filtering
      channels_to_list = channels_to_list.filter(ch => ch.data.frozen === globalState.search.archive);
      if (globalState.search.unread === true) channels_to_list = channels_to_list.filter(ch_item => countUnreadPerChannel(ch_item) > 0);
      // sorting
      if (globalState.drawer.tab === 'prime') channels_to_list.sort(sort_channels_to_list_prime);
      else channels_to_list.sort(sort_channels_to_list);
      // search
      if (globalState.drawer.tab === 'prime' && globalState.search.input !== '' && globalState.search.input.length > 0) {
        const options = {
          includeScore: true,
          includeMatches: true,
          threshold: 0.2,
          keys: [ 'data.name', 'data.unitCode', 'data.firstName', 'data.lastName', 'data.companyName', 'data.phoneNumberRaw',
          {
            name: 'parsedPhoneNumber01',
            getFn: (channel) => {
              if (channel.data.countryCode && channel.data.phoneNumberRaw) {
                const intPhoneNumber = '+' + channel.data.countryCode + channel.data.phoneNumberRaw;
                const parsedPhoneNumber = parseMax(intPhoneNumber, { defaultCountry: '', extract: false });
                const searchPhoneNumber = `+${parsedPhoneNumber.countryCallingCode} ${parsedPhoneNumber.formatNational()}`;
                if (parsedPhoneNumber) return searchPhoneNumber;
              }
            }
          },
          {
            name: 'parsedPhoneNumber02',
            getFn: (channel) => {
              if (channel.data.countryCode && channel.data.phoneNumberRaw) {
                const intPhoneNumber = '+' + channel.data.countryCode + channel.data.phoneNumberRaw;
                const parsedPhoneNumber = parseMax(intPhoneNumber, { defaultCountry: '', extract: false });
                const searchPhoneNumber = `${parsedPhoneNumber.number}`;
                if (parsedPhoneNumber) return searchPhoneNumber;
              }
            }
          },
          {
            name: 'parsedPhoneNumber03',
            getFn: (channel) => {
              if (channel.data.countryCode && channel.data.phoneNumberRaw) {
                const intPhoneNumber = '+' + channel.data.countryCode + channel.data.phoneNumberRaw;
                const parsedPhoneNumber = parseMax(intPhoneNumber, { defaultCountry: '', extract: false });
                const searchPhoneNumber = `${parsedPhoneNumber.formatInternational()}`;
                if (parsedPhoneNumber) return searchPhoneNumber;
              }
            }
          }
          ]
        };
        const fuseInit = new Fuse(channels_to_list, options)
        const fuseResult = fuseInit.search(globalState.search.input);
        channels_to_list = fuseResult.map(res => res.item);
      }
      if (globalState.drawer.tab === 'team' && globalState.search.input !== '' && globalState.search.input.length > 0) {
        const options = { includeScore: true, includeMatches: true, threshold: 0.2, keys: ['data.name']};
        const fuseInit = new Fuse(channels_to_list, options)
        const fuseResult = fuseInit.search(globalState.search.input);
        channels_to_list = fuseResult.map(res => res.item);
      }
      if (globalState.drawer.tab === 'direct' && globalState.search.input !== '' && globalState.search.input.length > 0) {
        const options = {
          includeScore: true,
          includeMatches: true,
          threshold: 0.2,
          keys: [
            {
              name: 'members',
              getFn: (channel) => {
                let members = Object.values(channel.state.members).filter(({ user }) => user.id !== client.userID);
                let member = `${members[0]?.user.name || members[0]?.user.id}`;
                return member;
              }
            }
          ]
        };
        const fuseInit = new Fuse(channels_to_list, options)
        const fuseResult = fuseInit.search(globalState.search.input);
        channels_to_list = fuseResult.map(res => res.item);
      }
      return channels_to_list;
    }
    catch (error) {
      console.log(error);
    }
  };
  const [channelsToList, setChannelsToList] = React.useState([]);
  React.useEffect(() => {
    if (appSettings.debug === true) {
      console.log('Debug', true); console.log('client', client); console.log('state', globalState); console.log('channel', channel);
    } else console.log('Debug', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettings.debug]);
  React.useEffect(() => {
    setBadgeCounts(countAllUnreadChannels(client.activeChannels));
    setChannelsToList(processActiveChannels(client.activeChannels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.search.unread, globalState.search.archive, globalState.idxs.ch_list]);
  React.useEffect(() => {
    setChannelsToList(processActiveChannels(client.activeChannels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.drawer.tab, globalState.search.input]);
  React.useEffect(() => {
    let totalBadgeCounts = 0;
    Object.keys(badgeCounts).map(item => totalBadgeCounts += badgeCounts[item]);
    totalBadgeCounts === 0 ? document.title = 'Xpel Chat' : document.title = `(${totalBadgeCounts}) Xpel Chat` ;
  }, [badgeCounts])
  React.useEffect(() => {
    if (appSettings.debug === true) console.log('appSettings', appSettings);
    const eventTypesToHandle = [
      'message.new', 'notification.message_new', 'channels.queried', 'message.read', 'notification.mark_read',
      'member.added', 'member.removed', 'notification.added_to_channel', 'notification.removed_from_channel', 'channel.updated', 'user.presence.changed', 'channel.deleted', 'channel.kicked'];
    let eventListener = client.on(async (event) => {
      if (event.type === 'connection.changed' && event.online === false) {
        let { title, body } = {title: 'Connection issue', body: 'Connectivity issue detected, please log in again'};
        let desktopNotification = new Notification(title, { body: body });
        desktopNotification.onclick = (e) => {
          e.preventDefault();
          window.focus();
          desktopNotification.close();
        };
        PlayNotificationSound();
        onClickDisconnect();
      }
      if (event.type === 'message.new' && event.user.id !== client.userID && event.message.silent === false) {
        if ((event.user.id.startsWith('driver') === true || event.user.id === 'sms_service') || event.channel_type !== 'prime') {
          if (document.visibilityState === 'hidden') {
            let showDesktopNotification = false;
            if (event.channel_type === 'prime' && appSettings.nPrime === true) showDesktopNotification = true;
            if (event.channel_type === 'team' && appSettings.nTeam === true) showDesktopNotification = true;
            if (event.channel_type === 'direct' && appSettings.nDirect === true) showDesktopNotification = true;
            if (showDesktopNotification === true) {
              let { title, body } = notificationText(event, client);
              let desktopNotification = new Notification(title, { body: body });
              desktopNotification.onclick = (e) => {
                e.preventDefault();
                window.focus();
                setGlobalState.drawer({switch: true, tab: event.channel_type});
                setActiveChannel(client.activeChannels[event.cid]);
                desktopNotification.close();
              };
            }

          }
          else {
            if (event.channel_id !== channel?.id) {
              if (event.channel_type === 'prime' && appSettings.sPrime === true) PlayNotificationSound();
              if (event.channel_type === 'team' && appSettings.sTeam === true) PlayNotificationSound();
              if (event.channel_type === 'direct' && appSettings.sDirect === true) PlayNotificationSound();
            }
          }
        }
      }
      if (event.type === 'notification.message_new' && Object.keys(client.activeChannels).includes(event.cid) === false && event.message.silent === false && event.message.user.id !== 'service_undefined') {
        let channelToRetrieve = client.channel(event.channel_type, event.channel_id, {});
        await channelToRetrieve.watch({ messages: { limit: 25 } });
        if (document.visibilityState === 'hidden') {
          let showDesktopNotification = false;
          if (event.channel_type === 'prime' && appSettings.nPrime === true) showDesktopNotification = true;
          if (event.channel_type === 'team' && appSettings.nTeam === true) showDesktopNotification = true;
          if (event.channel_type === 'direct' && appSettings.nDirect === true) showDesktopNotification = true;
          if (showDesktopNotification === true) {
            let { title, body } = notificationText(event, client);
            let desktopNotification = new Notification(title, { body: body });
            desktopNotification.onclick = (e) => {
              e.preventDefault();
              window.focus();
              setActiveChannel(channelToRetrieve);
              setGlobalState.drawer({switch: true, tab: event.channel_type});
              desktopNotification.close();
            };
          }
        }
        else {
          if (event.channel_id !== channel?.id) {
            if (event.channel_type === 'prime' && appSettings.sPrime === true) PlayNotificationSound();
            if (event.channel_type === 'team' && appSettings.sTeam === true) PlayNotificationSound();
            if (event.channel_type === 'direct' && appSettings.sDirect === true) PlayNotificationSound();
          }
        }
      }
      if (event.type === 'member.added' || event.type === 'notification.added_to_channel') {
        if (client.userID === event.member.user_id) {
          let addedToChannel = client.channel(event.channel_type, event.channel_id, {});
          await addedToChannel.watch({ messages: { limit: 25 } });
        }
      }
      if (event.type === 'member.removed' || event.type === 'notification.removed_from_channel') {
        if (client.userID === event.member.user_id) {
          if (channel && channel.cid === event.cid) setActiveChannel();
          if (client.activeChannels[event.cid]) delete client.activeChannels[event.cid];
          setGlobalState.chList(event.cid);
        }
      }
      if (event.type === 'channel.deleted') {
        if (channel && channel.cid === event.cid) setActiveChannel();
        if (client.activeChannels[event.cid]) delete client.activeChannels[event.cid];
        setGlobalState.chList(event.cid);
      }
      if (eventTypesToHandle.includes(event.type) === true) {
        appSettings.debug === true && console.log(event.type, event);
        setBadgeCounts(countAllUnreadChannels(client.activeChannels));
        setChannelsToList(processActiveChannels(client.activeChannels));
      }
    });
    return () => eventListener.unsubscribe();
  })
  React.useEffect(() => { if (is_desktop === false) { setDrawerWidth(parentBoxRef.current.offsetWidth - 65) } }, [is_desktop])
  const onClickChannelTab = (newTab) => setGlobalState.drawer(newTab);
  const onClickDisconnect = () => setGlobalState.disconnect();
  const onClickUnreadFilter = () => setGlobalState.search('unread');
  const onClickArchiveFilter = async () => {
    try {
      if (globalState.search.archive === false) {
        const archiveBody = { 'token': globalState.app_config.authToken, types: ['prime']};
        const archiveRequest = await axios.post(globalState.app_config.authEnv+'/channels/list?isOnlyFrozen=true', archiveBody);
        if (archiveRequest.status === 200 && archiveRequest.data && archiveRequest.data.result === true && typeof archiveRequest.data.channels === 'object' && Array.isArray(archiveRequest.data.channels) === true && archiveRequest.data.channels !== null) {
          if (archiveRequest.data.channels.length > 0 && typeof archiveRequest.data.channels[0]['cid'] === 'string') {
            let archivedMap = {};
            archiveRequest.data.channels.map(ch_item => archivedMap[ch_item.cid] = { ...ch_item, state: {...ch_item.state, last_message_at: ch_item.state.last_message_at ? new Date(ch_item.state.last_message_at) : new Date(0)} });
            setGlobalState.chList(archivedMap);
          }
        }
        else throw Error('error-archive-request');
      }
      setGlobalState.search('archive');
    }
    catch (err) {
      console.log(err);
    }
  };
  const handleInputSearch = (e) => {
    e.preventDefault();
    setGlobalState.search({type: 'input', value: e.target.value});
    setChannelsToList(processActiveChannels(client.activeChannels));
  };
  const resetInputSearch = () => {
    setGlobalState.search({type: 'input', value: ''});
    setChannelsToList(processActiveChannels(client.activeChannels));
  };
  return (
    <Box ref={parentBoxRef} sx={{ display: 'flex', width: '100%' }}>
      <CssBaseline />
      <MuiDrawer variant='permanent' sx={{width: 65}}>
        <DrawerHeader sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <SideBarLogoXpel />
        </DrawerHeader>
        <List>
          <ListItem key={'prime'} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }} onClick={() => onClickChannelTab('prime')} selected={(globalState.drawer.is_open === true || is_desktop === false) && globalState.drawer.tab === 'prime'}>
              <Tooltip title={`Service`} placement='right'>
                <ListItemIcon sx={{minWidth: 0, mr: 'auto', justifyContent: 'center'}}>
                  <Badge color='error' badgeContent={badgeCounts.prime} max={50} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} sx={{'& .MuiBadge-standard': {fontWeight: 700, fontSize: '0.95rem', minWidth: '24px', height: '24px', borderRadius: '12px', top: -4, backgroundColor: '#ff8787'}}}>
                    <SupportAgentIcon />
                  </Badge>
                </ListItemIcon>
              </Tooltip>
            </ListItemButton>
          </ListItem>
          <ListItem key={'team'} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }} onClick={() => onClickChannelTab('team')} selected={(globalState.drawer.is_open === true || is_desktop === false) && globalState.drawer.tab === 'team'}>
              <Tooltip title={`Groups`} placement='right'>
                <ListItemIcon sx={{minWidth: 0, mr: 'auto', justifyContent: 'center'}}>
                  <Badge color='error' badgeContent={badgeCounts.team} max={10} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} sx={{'& .MuiBadge-standard': {fontWeight: 700, fontSize: '0.95rem', minWidth: '24px', height: '24px', borderRadius: '12px', top: -4, backgroundColor: '#ff8787'}}}>
                    <GroupsIcon />
                  </Badge>
                </ListItemIcon>
              </Tooltip>
            </ListItemButton>
          </ListItem>
          <ListItem key={'direct'} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }} onClick={() => onClickChannelTab('direct')} selected={(globalState.drawer.is_open === true || is_desktop === false) && globalState.drawer.tab === 'direct'}>
              <Tooltip title={`Directs`} placement='right'>
                <ListItemIcon sx={{minWidth: 0, mr: 'auto', justifyContent: 'center'}}>
                  <Badge color='error' badgeContent={badgeCounts.direct} max={10} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} sx={{'& .MuiBadge-standard': {fontWeight: 700, fontSize: '0.95rem', minWidth: '24px', height: '24px', borderRadius: '12px', top: -4, backgroundColor: '#ff8787'}}}>
                    <PeopleIcon />
                  </Badge>
                </ListItemIcon>
              </Tooltip>
            </ListItemButton>
          </ListItem>
        </List>
        <Box sx={{display: 'flex', flexGrow: 1}} />
        <List>
          <CampaignModal/>
          <SettingsModal appSettings={appSettings} setAppSettings={setAppSettings}/>
          <ListItem key={'disconnect'} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }} onClick={onClickDisconnect}>
              <Tooltip title={`Disconnect`} placement='right'>
                <ListItemIcon sx={{minWidth: 0, mr: 'auto', justifyContent: 'center'}}>
                  <LogoutIcon />
                </ListItemIcon>
              </Tooltip>
            </ListItemButton>
          </ListItem>
        </List>
      </MuiDrawer>
      <MuiDrawer variant='persistent' open={globalState.drawer.is_open} sx={{
          width: drawerWidth,
          flexShrink: 0,
          zIndex: theme.zIndex.drawer - 1,
          '& .MuiDrawer-paper': { marginLeft: '65px', width: drawerWidth, height: '100%', boxSizing: 'border-box', overflowX: 'hidden' }
        }}>
        <Box component='div' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '62px', width: 'auto' }}>
          <IconButton sx={{ p: '10px' }} aria-label='filter channels' onClick={onClickUnreadFilter}>
            <Tooltip title='Unread channels' placement='bottom'><FilterIcon color={globalState.search.unread === true ? 'primary' : 'default'}/></Tooltip>
          </IconButton>
          <InputBase sx={{ ml: 1, flex: 1 }} placeholder='Search...' type='text' onChange={handleInputSearch} value={globalState.search.input} multiline={false}  inputProps={{ 'aria-label': 'search channels or users' }}/>
          { globalState.search.input !== '' && <IconButton type='button' sx={{ p: '10px' }} aria-label='reset search' onClick={resetInputSearch}><ClearIcon /></IconButton>}
          <Divider sx={{ height: 28, m: 0.5 }} orientation='vertical' />
          { globalState.drawer.tab === 'prime' &&
          <IconButton sx={{ p: '10px' }} aria-label='Archive' onClick={onClickArchiveFilter}>
            <Tooltip title='Archive' placement='bottom'><ArchiveIcon color={globalState.search.archive === true ? 'error' : 'default'} sx={{color: globalState.search.archive === true ? '#ff8787' : ''}}/></Tooltip>
          </IconButton> }
          { globalState.drawer.tab === 'prime' && <PrimeAddModal /> }
          { globalState.drawer.tab === 'direct' && <DirectAddModal /> }
          { globalState.drawer.tab === 'team' && <TeamAddModal /> }
        </Box>
        <Divider orientation='horizontal' />
        <FixedSizeList height={window.innerHeight} width={drawerWidth} itemSize={96} itemData={channelsToList} itemCount={channelsToList.length} overscanCount={4}>
          {(listProps) => {
            const { data, index, style } = listProps;
            const ch_item = data[index];
            let itemToList = {mainName: '', author: '', message: ch_item.state.messages ? ch_item.state.messages[ch_item.state.messages.length - 1] : [], onlinemember: false };
            if (ch_item.data.type === 'prime') itemToList.mainName = getPrimeName(ch_item);
            else if (ch_item.data.type === 'team') itemToList.mainName = `${ch_item.data.name || ch_item.data.id}`;
            else if (ch_item.data.type === 'direct') {
              const members = Object.values(ch_item.state.members).filter(({ user }) => user.id !== client.userID);
              itemToList.mainName = `${members[0]?.user.name || members[0]?.user.id || 'Deleted contact'}`;
              itemToList.onlinemember = members[0]?.user.online === true;
            }
            itemToList.mainName = itemToList.mainName.length > 26 ? `${itemToList.mainName.substring(0,26)}...` : itemToList.mainName;
            if (typeof itemToList.message === 'undefined') itemToList.message = `— Begin the conversation...`;
            if (typeof itemToList.message !== 'undefined' && typeof itemToList.message.text === 'string') {
              itemToList.author = client.userID === itemToList.message.user.id ? 'Me' : itemToList.message.user.name || itemToList.message.user.id;
              let withAttachment = itemToList.message.attachments.length > 0;
              itemToList.message = `— ${itemToList.message.text.length > 0 ? itemToList.message.text.substring(0,50) : withAttachment === true ? 'Attachment(s)' : 'Empty'}`;
            }
            const channelListItem = async () => {
              if (ch_item.cid === channel?.cid) setActiveChannel();
              else {
                if (Object.keys(client.activeChannels).includes(ch_item.cid) === true && ch_item.initialized === true) setActiveChannel(ch_item);
                else {
                  let ch_target = client.channel(ch_item.data.type, ch_item.data.id, {});
                  await ch_target.watch({ messages: { limit: 25 } });
                  setActiveChannel(ch_target);
                }
              }
              if (is_desktop === false) setGlobalState.drawer(globalState.drawer.tab);
            };
            return (
              <>
                <ListItem alignItems='flex-start' style={style} key={ch_item.cid} component='div' disablePadding divider={true} selected={ch_item.cid === channel?.cid ? true : false}>
                  <ListItemButton onClick={channelListItem} sx={{height: '100%'}}>
                    { (ch_item.data.type === 'prime' || ch_item.data.type === 'team') &&
                      <ListItemAvatar sx={{minWidth: '20px'}}>
                        <Badge color='error' anchorOrigin={{ vertical: 'top', horizontal: 'left' }} badgeContent={countUnreadPerChannel(ch_item)} sx={{'& .MuiBadge-standard': {fontWeight: 700, fontSize: '0.95rem', minWidth: '24px', height: '24px', borderRadius: '12px', top: 0, backgroundColor: '#ff8787'}}} />
                      </ListItemAvatar> }
                    { ch_item.data.type === 'direct' &&
                      <ListItemAvatar>
                        <Badge color='error' anchorOrigin={{ vertical: 'top', horizontal: 'left' }} badgeContent={countUnreadPerChannel(ch_item)} sx={{'& .MuiBadge-standard': {fontWeight: 700, fontSize: '0.95rem', minWidth: '24px', height: '24px', borderRadius: '12px', top: -4, backgroundColor: '#ff8787'}}}>
                          <Badge color={ itemToList.onlinemember === true ? 'success' : 'error'} variant='dot' overlap='circular' anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} sx={itemToList.onlinemember === false ? {'& .MuiBadge-dot' : { visibility: 'hidden' }} : {}}>
                            <Avatar sx={{textTransform: 'capitalize'}}>{itemToList.mainName.charAt(0)}</Avatar>
                          </Badge>
                        </Badge>
                      </ListItemAvatar> }
                    <ListItemText
                      primary={<Typography noWrap={true} sx={{ display: 'block' }} fontWeight={600}>{itemToList.mainName}</Typography>}
                      secondary={<>
                          <Typography noWrap={true} sx={{ display: 'block' }} component='span' variant='body2' color='text.primary'>{itemToList.author}</Typography>
                          <Typography noWrap={true} sx={{ display: 'block' }} component='span' color='grey.500' variant='body2' fontStyle='italic'>{itemToList.message}</Typography>
                        </>}
                    />
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-evenly', height: '100%'}}>
                      { ch_item.data.type === 'prime' && ch_item.data.frozen === true && <Chip size='small' label={<ArchiveIcon fontSize='small' sx={{marginBottom: '-4px'}} />} color={'error'} sx={{ml: 0.2, backgroundColor: '#ff8787'}}/>}
                      { ch_item.data.type === 'prime' && typeof ch_item.data.unitCode === 'string' && ch_item.data.unitCode !== '' && <ChipUnitCodes channel={ch_item} onHeader={false}/> }
                      { ch_item.data.type === 'prime' && <Chip size='small' label={ch_item.data.channel_type && ch_item.data.channel_type === 'driver' ? 'unit' : ch_item.data.recordType} sx={{color: 'white', textTransform: 'capitalize', backgroundColor: ch_item.data.recordType ? prime_badgeColor(ch_item.data.recordType.toLowerCase()) : prime_badgeColor(ch_item.data.channel_type)}}/>}
                      { ch_item.data.type !== 'prime' && <Chip size='small' label={ch_item.data.type === 'team' ? 'Group' : 'Direct'} variant='outlined' color='info'/>}
                    </Box>
                  </ListItemButton>
                </ListItem>
              </>
            );
          }}
        </FixedSizeList>
      </MuiDrawer>
      <Main open={globalState.drawer.is_open} drawer_width={drawerWidth} is_desktop={is_desktop.toString()}>
        <ChannelContainer/>
      </Main>
    </Box>
  );
};

export default ChatDashboard;
